<template>
  <div v-if="!showAdMetricsChartDataLoadingIndicator && adMetricsChartData.length === 0" class="no-data flex column justify-center items-center q-pa-lg" style="width: 100%;">
    <q-icon name="insert_chart" size="3rem" />
    <p class="text-center q-mt-md">
      No data to display
    </p>
  </div>
  <div v-else class="flex rounded-borders report-card row q-ml-none">
    <VChart
      class="chart line-chart"
      :option="chartOptions"
      :loading="showAdMetricsChartDataLoadingIndicator"
      :loading-options="{
        text: 'Loading',
        color: '#6C50D3'
      }"
      :autoresize="true"
    />
  </div>
</template>
<script setup>
import dayjs from 'dayjs'
import {storeToRefs} from 'pinia'
import { ref, watch, provide } from 'vue'

import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

import { formatComma } from '@utils/helpers'
import { useReportsStore } from '@stores/reports'
import { useVuetifyColorsRgb } from '@composables/useVuetifyColors'

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
])

provide(THEME_KEY, 'light')

const reportsStore = useReportsStore()
const { showAdMetricsChartDataLoadingIndicator, adMetricsChartData, selectedMetric } = storeToRefs(reportsStore)

watch(() => selectedMetric.value, () => {
  chartOptions.value.yAxis.name = selectedMetric.value.label
})

watch(() => adMetricsChartData.value, () => {
  if (adMetricsChartData.value.length > 0) {
    chartOptions.value.series.data = adMetricsChartData.value
  }
})

const { primaryRgb } = useVuetifyColorsRgb()

const chartOptions = ref({
  grid: {
    top: '60px',
    bottom: '50px',
    left: '63px',
    right: '80px'
  },
  xAxis: {
    type: 'time',
    name: 'Date',
    boundaryGap: false,
    backgroundColor: '#6C50D3',
    nameTextStyle: {
      color: 'black',
      fontSize: 16,
      fontWeight: '600'
    },
    axisPointer: {
      show: true,
      type: 'line',
      snap: true,
      lineStyle: {
        type: 'solid',
        color: primaryRgb,
        width: 2,
        opacity: 1
      },
      label: {
        show: false,
        color: '#3A3A3A',
        backgroundColor: 'none',
        formatter: ((params) => {
          const date = new Date(params.value)
          return dayjs(date).format('MMM DD, YYYY')
        }),
      }
    },
    axisLabel: {
      formatter: ((value) => {
        const date = new Date(value)
        return dayjs(date).format('MMM DD')
      }),
      fontSize: 12,
      fontWeight: '600'
    }
  },
  yAxis: {
    type: 'value',
    name: selectedMetric.value.label,
    nameTextStyle: {
      color: 'black',
      fontSize: 16,
      fontWeight: '600'
    },
    axisLabel: {
      fontSize: 12,
      fontWeight: '600'
    }
  },
  tooltip: {
    show: true,
    trigger: 'item',
    renderMode: 'html',
    position: function(point) {
      // fixed at top
      return [point[0], '10%']
    },
    extraCssText: 'box-shadow: none;',
    formatter: ((params) => {
      if (params[0]) {   
        const date = new Date(params[0]?.data[0])  
        let html = `<div style='text-align: center; font-size: 12px;'>
        <span style='font-weight: bold; color: #3A3A3A'>${dayjs(date).format('MMM DD, YYYY')}</span>
        <br>
        <span style='color: #6C50D3'>${formatComma(params[0]?.data[1])}</span>`
        return html
      }
    }),
  },
  series: {
    type: 'line',
    lineStyle: {
      color: primaryRgb,
      width: 4,
    },
    itemStyle: {
      color: primaryRgb,
    },
    data: adMetricsChartData.value
  }
})
</script>

<style lang="scss" scoped>
.report-card {
  min-height: 500px;
}
.chart {
  width: 100%;
  height: 100vh;
  min-height: 500px;
  max-height: 600px;
}
label.q-field {
  padding-bottom: 0 !important;
}
.totals .text-h5 {
  color: #6C50D3;
}
</style>

